import { useServerFetch } from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { MainContent } from '../shared-mui-components/MainContent.tsx';
import { MaterialHeader } from '../layout/components/MaterialHeader.tsx';
import { MaterialTitle } from '../layout/components/MaterialTitle.tsx';
import { Circle, Error, FilterAlt } from '@mui/icons-material';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import { SeparationBar } from '../layout/components/SeparationBar.tsx';
import { OperatorAsyncLoadedSection } from '../app/components/OperatorAsyncLoadedSection.tsx';
import { LoadingSpinnerPresets } from 'dg-web-shared/common/components/material-ui/PresetLoadingSpinner.tsx';
import { EmptyResults } from '../shared-mui-components/ListMessages.tsx';
import {
    Box,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import { ProductOptionType } from 'product-shared/product-template/ProductOptions.tsx';
import { Message } from 'dg-web-shared/lib/Localized.ts';
import { EmDash } from 'dg-web-shared/lib/Punctuation.ts';
import { useNavigate } from 'react-router';
import { OperatorQuotaOutlet } from './OperatorQuotasDetail.tsx';
import { useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import { Language } from 'dg-web-shared/lib/Text.ts';

interface QuotaListItem {
    contractQuotaPartitionId: number;
    quotaStatus: OperatorQuotaStatus | null;
    quotaProducts: QuotaProductsListItem[];
    currentLimit: number | null;
    currentlyUsed: number | null;
}

interface QuotaProductsListItem {
    productName: Message;
    type: ProductOptionType;
}

const enum OperatorQuotaStatus {
    NORMAL = 'NORMAL',
    FILLING = 'FILLING',
    FULL = 'FULL',
    OVER_CAPACITY = 'OVER_CAPACITY',
}

export function OperatorQuotasOverview() {
    const [quotasListResultState, refetchQuotasList] = useServerFetch<
        QuotaListItem[],
        object
    >(
        () => ({
            url: `/ui-api/operator-account/quotas`,
        }),
        {},
    );
    const { language } = useLanguage();

    return (
        <MainContent>
            <MaterialHeader editMode={false}>
                <MaterialTitle
                    Icon={FilterAlt}
                    title={
                        <Localized
                            de="Kontingente"
                            fr="Contingents"
                            it="Contingenti"
                            en="Quotas"
                        />
                    }
                />
            </MaterialHeader>
            <SeparationBar />
            <OperatorAsyncLoadedSection
                requestState={quotasListResultState}
                pendingLoaderPreset={
                    LoadingSpinnerPresets.FillAllSpaceAndCenter
                }
                render={quotasList => {
                    if (quotasList.length === 0) {
                        return <EmptyResults hasFilter={false} />;
                    }
                    return (
                        <TableContainer>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            <Localized
                                                de="Status"
                                                fr="État"
                                                it="Stato"
                                                en="State"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <ContractTypeTitleName
                                                quotaProducts={quotasList.flatMap(
                                                    q => q.quotaProducts,
                                                )}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Localized
                                                de="Aktuelle Limite"
                                                fr="Limite actuelle"
                                                it="Limite attuale"
                                                en="Current limit"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Localized
                                                de="Derzeit gültig"
                                                fr="Actuellement valables"
                                                it="Attualmente validi"
                                                en="Currently valid"
                                            />
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {quotasList
                                        .sort((a, b) =>
                                            getFirstSortedProductName(
                                                a,
                                                language,
                                            ).localeCompare(
                                                getFirstSortedProductName(
                                                    b,
                                                    language,
                                                ),
                                            ),
                                        )
                                        .map(quotasListItem => (
                                            <OperatorQuotaRow
                                                key={
                                                    quotasListItem.contractQuotaPartitionId
                                                }
                                                quotasListItem={quotasListItem}
                                                language={language}
                                            />
                                        ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    );
                }}
            />
            <OperatorQuotaOutlet
                context={{ refetchQuota: refetchQuotasList }}
            />
        </MainContent>
    );
}

function OperatorQuotaRow({
    quotasListItem,
    language,
}: {
    quotasListItem: QuotaListItem;
    language: Language;
}) {
    const navigate = useNavigate();
    return (
        <TableRow
            hover
            sx={{ cursor: 'pointer' }}
            onClick={() =>
                navigate(`${quotasListItem.contractQuotaPartitionId}`)
            }
        >
            <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <QuotaAvailabilityStatus
                        quotaStatus={quotasListItem.quotaStatus}
                    />
                </Box>
            </TableCell>
            <TableCell>
                <Stack direction={'column'}>
                    {quotasListItem.quotaProducts.length > 0
                        ? quotasListItem.quotaProducts
                              .sort((a, b) =>
                                  a.productName[language].localeCompare(
                                      b.productName[language],
                                  ),
                              )
                              .map(quotaProduct => (
                                  <Box key={quotaProduct.productName.de}>
                                      <Localized
                                          {...quotaProduct.productName}
                                      />
                                  </Box>
                              ))
                        : EmDash}
                </Stack>
            </TableCell>
            <TableCell>{quotasListItem.currentLimit ?? EmDash}</TableCell>
            <TableCell>{quotasListItem.currentlyUsed ?? EmDash}</TableCell>
        </TableRow>
    );
}

function getFirstSortedProductName(
    item: QuotaListItem,
    language: Language,
): string {
    return (
        [...item.quotaProducts].sort((a, b) =>
            a.productName[language].localeCompare(b.productName[language]),
        )[0]?.productName[language] || ''
    );
}

function QuotaAvailabilityStatus({
    quotaStatus,
}: {
    quotaStatus: OperatorQuotaStatus | null;
}) {
    switch (quotaStatus) {
        case OperatorQuotaStatus.NORMAL:
            return <Circle sx={{ color: 'success.main' }} />;
        case OperatorQuotaStatus.FILLING:
            return <Circle sx={{ color: 'warning.main' }} />;
        case OperatorQuotaStatus.FULL:
            return <Circle sx={{ color: 'error.main' }} />;
        case OperatorQuotaStatus.OVER_CAPACITY:
            return <Error sx={{ color: 'error.main' }} />;
        case null:
            return <Box sx={{ paddingLeft: '7px' }}>{EmDash}</Box>;
    }
}

function ContractTypeTitleName({
    quotaProducts,
}: {
    quotaProducts: QuotaProductsListItem[];
}) {
    const hasProducts = quotaProducts.some(
        quotaProduct => quotaProduct.type === ProductOptionType.PRODUCT,
    );
    const hasPermits = quotaProducts.some(
        quotaProduct => quotaProduct.type === ProductOptionType.PERMIT,
    );
    if (hasProducts && !hasPermits) {
        return (
            <Localized de="Produkt" fr="Produit" it="Prodotto" en="Product" />
        );
    }
    if (!hasProducts && hasPermits) {
        return (
            <Localized
                de="Bewilligung"
                fr="Autorisation"
                it="Autorizzazione"
                en="Permit"
            />
        );
    }
    return (
        <Localized
            de="Produkt / Bewilligung"
            fr="Produit / Autorisation"
            it="Prodotto / Autorizzazione"
            en="Product / Permit"
        />
    );
}
